import { useState, type ReactNode } from "react";
import type { QuizQuestion } from "../scenario/types";
import { renderInlineMarkdown } from "../lib/markdown";
import "./results-compact.css";

type Dim = { dimension: string; label: string; percent: number };
type Ev = { id: string; at: number; kind: string; label: string };

/**
 * End screen of every 3D course (rendered by Debrief.tsx after the quiz).
 * Results = one glass panel: score ring + verdict, a table with one row per
 * question (wrong rows highlighted, the first wrong one open), the exposure
 * bars (or the risk-meter review for courses that use it), then
 * "Finish course" -> a thank-you page that confirms the course is complete,
 * with a restart option.
 */
export function ResultsCompact({
  title,
  quiz,
  answers,
  passed,
  correct,
  dims,
  evidence,
  choices,
}: {
  title: string;
  quiz: QuizQuestion[];
  answers: Record<string, string>;
  passed: boolean;
  correct: number;
  dims: Dim[] | null;
  evidence: Ev[];
  /** risk-meter courses: the walkthrough list of the learner's choices. */
  choices?: { count: number; body: ReactNode } | null;
}) {
  const rows = quiz.map((q, i) => {
    const chosen = q.options.find((o) => o.id === answers[q.id]);
    return { q, i, chosen, ok: !!chosen?.correct, right: q.options.find((o) => o.correct) };
  });
  const firstWrong = rows.find((r) => !r.ok)?.q.id ?? null;
  const [open, setOpen] = useState<string | null>(firstWrong);
  const [showEv, setShowEv] = useState(false);
  const [showChoices, setShowChoices] = useState(false);
  const [done, setDone] = useState(false);
  const total = quiz.length;
  const pct = total ? Math.round((correct / total) * 100) : 100;

  if (done) {
    return (
      <div className="debrief rc-root">
        <div className="rc-panel rc-thanks">
          <div className="rc-thanks-badge" aria-hidden="true">
            <svg viewBox="0 0 64 64"><circle cx="32" cy="32" r="28" /><path d="M20 33l8 8 16-17" /></svg>
          </div>
          <span className="rc-kicker">Course complete</span>
          <h2>Thank you for completing<br />{title}</h2>
          <p className="rc-thanks-lead">
            You finished the course to the end. Your result has been recorded: quiz {correct} of {total}
            {passed ? ", passed." : ". Have another go whenever you like."}
          </p>
          <ul className="rc-thanks-facts">
            <li><b>{correct}/{total}</b><span>Quiz</span></li>
            <li><b>{passed ? "Passed" : "Practice"}</b><span>Result</span></li>
            <li><b>{new Date().toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</b><span>Completed</span></li>
          </ul>
          <p className="rc-thanks-note">You can close this window now.</p>
          <div className="rc-actions">
            <button type="button" className="rc-btn" onClick={() => setDone(false)}>Back to results</button>
            <button type="button" className="rc-btn rc-btn--ghost" onClick={() => window.location.reload()}>Restart course</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="debrief rc-root">
      <div className="rc-panel">
        <header className="rc-head">
          <div className={`rc-ring ${passed ? "is-pass" : "is-fail"}`} style={{ ["--p" as string]: `${pct}` }}>
            <b>{correct}/{total}</b>
            <small>quiz</small>
          </div>
          <div className="rc-head-c">
            <span className="rc-kicker">Results</span>
            <h2>{title}</h2>
            <span className={`rc-verdict ${passed ? "is-pass" : "is-fail"}`}>{passed ? "Passed" : "Needs practice"}</span>
            <p>
              {passed
                ? "You spotted the key risks. The table shows each answer; open a row for the explanation."
                : "Some answers missed the point. The wrong ones are highlighted below, open each to see why."}
            </p>
          </div>
        </header>

        <div className="rc-table" role="table" aria-label="Quiz review">
          {rows.map(({ q, i, chosen, ok, right }) => {
            const isOpen = open === q.id;
            return (
              <div key={q.id} className={`rc-row ${ok ? "is-ok" : "is-bad"}${isOpen ? " is-open" : ""}`} role="row">
                <button type="button" className="rc-row-h" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : q.id)}>
                  <span className="rc-n">{i + 1}</span>
                  <span className="rc-t">{q.title ?? q.prompt}</span>
                  <span className="rc-a">{chosen ? chosen.label : "Not answered"}</span>
                  <span className={`rc-s ${ok ? "is-ok" : "is-bad"}`}>{ok ? "\u2713 Correct" : "\u2717 Wrong"}</span>
                  <span className="rc-chev" aria-hidden="true">{isOpen ? "\u25B2" : "\u25BC"}</span>
                </button>
                {isOpen && (
                  <div className="rc-row-b">
                    <p className="rc-prompt">{q.prompt}</p>
                    <div className="rc-ans">
                      <span>Your answer</span>
                      <b className={ok ? "is-ok" : "is-bad"}>{chosen ? chosen.label : "Not answered"}</b>
                    </div>
                    {!ok && right && (
                      <div className="rc-ans">
                        <span>Correct answer</span>
                        <b className="is-ok">{right.label}</b>
                      </div>
                    )}
                    {q.rationale && <p className="rc-why" dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(q.rationale) }} />}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {dims && (
          <section className="rc-exp">
            <div className="rc-exp-h">
              <span>What this run exposed to an attacker</span>
              {evidence.length > 0 && (
                <button type="button" className="rc-link" onClick={() => setShowEv((v) => !v)}>
                  {showEv ? "Hide details" : `Details (${evidence.length})`}
                </button>
              )}
            </div>
            <div className="rc-exp-dims">
              {dims.map((d) => (
                <div key={d.dimension} className="rc-dim">
                  <span>{d.label}</span>
                  <i><em className={`rc-fill rc-fill--${d.dimension}`} style={{ width: `${d.percent}%` }} /></i>
                </div>
              ))}
            </div>
            {showEv && (
              <ul className="rc-ev">
                {evidence.slice().reverse().map((it) => (
                  <li key={`${it.id}-${it.at}`} className={it.kind === "risk" ? "is-bad" : "is-ok"}>
                    <span aria-hidden="true">{it.kind === "risk" ? "\u26A0" : "\u2713"}</span>
                    {it.label}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {choices && (
          <section className="rc-exp">
            <div className="rc-exp-h">
              <span>Your choices during this run</span>
              <button type="button" className="rc-link" onClick={() => setShowChoices((v) => !v)}>
                {showChoices ? "Hide details" : `Details (${choices.count})`}
              </button>
            </div>
            {showChoices && (
              <div className="rc-choices">
                <p className="rc-note">Not part of your score: some choices were forced so you could see what a real attack looks like from the inside.</p>
                {choices.body}
              </div>
            )}
          </section>
        )}

        <footer className="rc-actions">
          <button type="button" className="rc-btn rc-btn--ghost" onClick={() => window.location.reload()}>Try again</button>
          <button type="button" className="rc-btn cf-pulse-target" onClick={() => setDone(true)}>Finish course</button>
        </footer>
      </div>
    </div>
  );
}
